import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import {
  ArrowLeft, Copy, History, Loader2, PenLine, Plus, ShieldCheck, Sparkles, Wand2,
} from "lucide-react";

const errMsg = (err, fallback) => formatApiError(err?.response?.data?.detail) || fallback;
const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

const DRAFT_TYPES = [
  { id: "email_cliente", label: "Email a cliente", hint: "reclami, solleciti, variazioni, benvenuto" },
  { id: "pec", label: "PEC formale", hint: "oggetto, riferimenti, distinti saluti" },
  { id: "interna", label: "Comunicazione interna", hint: "avvisi al team" },
  { id: "libero", label: "Testo libero", hint: "descrivi tu formato e contenuto" },
];
const TYPE_LABEL = Object.fromEntries(DRAFT_TYPES.map((t) => [t.id, t.label]));
const TONES = [
  { id: "formale", label: "Formale" },
  { id: "cordiale", label: "Cordiale" },
  { id: "deciso", label: "Deciso" },
];
const SCENARIO_CHIPS = {
  email_cliente: [
    ["Risposta a reclamo", "Il cliente [NOME] lamenta che "],
    ["Sollecito pagamento", "Sollecitare il pagamento della fattura n. [N] di [IMPORTO] scaduta il [DATA] al cliente [NOME]. "],
    ["Variazione contrattuale", "Comunicare al cliente [NOME] che dal [DATA] cambia "],
    ["Benvenuto", "Dare il benvenuto al nuovo cliente [NOME] appena attivato con "],
  ],
};
const REVISE_CHIPS = ["Più corta", "Più ferma", "Più cordiale", "Più formale"];

export default function Drafts() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const [draftType, setDraftType] = useState("email_cliente");
  const [tone, setTone] = useState("cordiale");
  const [context, setContext] = useState("");
  const [points, setPoints] = useState("");
  const [session, setSession] = useState(null);
  const [reviseText, setReviseText] = useState("");
  const [busy, setBusy] = useState(null); // generate | revise
  const [history, setHistory] = useState([]);

  const allowed = user?.role === "super_admin" || features?.ai_tools;
  const [historyFailed, setHistoryFailed] = useState(false);
  const loadHistory = () =>
    api.get("/ai/tools/drafts/sessions").then(({ data }) => { setHistory(data); setHistoryFailed(false); }).catch(() => setHistoryFailed(true));
  useEffect(() => { if (allowed) loadHistory(); }, [allowed]);

  if (features && !allowed) return <Navigate to="/services" replace />;

  const reset = () => {
    setSession(null); setContext(""); setPoints(""); setReviseText("");
    setDraftType("email_cliente"); setTone("cordiale");
  };

  const onGenerate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post("/ai/tools/drafts/sessions",
        { draft_type: draftType, tone, context, points });
      setSession(data);
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err, "Generazione non riuscita"));
    } finally { setBusy(null); }
  };

  const onRevise = async (instruction) => {
    setBusy("revise");
    try {
      const { data } = await api.post(`/ai/tools/drafts/sessions/${session.id}/revise`,
        { instruction });
      setSession((s) => ({ ...s, variants: data.variants, revisions: data.revisions }));
      setReviseText("");
      toast.success("Bozze riviste");
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err, "Revisione non riuscita"));
    } finally { setBusy(null); }
  };

  const copyVariant = (v) => {
    navigator.clipboard.writeText(v.text);
    toast.success("Bozza copiata: rileggila prima di inviarla");
  };

  const openHistory = async (id) => {
    try {
      const { data } = await api.get(`/ai/tools/drafts/sessions/${id}`);
      setSession(data);
      setDraftType(data.draft_type); setTone(data.tone);
      setContext(data.context); setPoints(data.points || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(errMsg(err, "Bozza non trovata")); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="drafts-page">
      <div>
        <Link to="/assistant" className="inline-flex items-center gap-1.5 text-[13px] text-text-secondary hover:text-text-primary transition-colors mb-4"
              data-testid="drafts-back">
          <ArrowLeft size={14} /> Assistente AI
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-medium tracking-tight text-text-primary flex items-center gap-2">
              <PenLine size={22} className="text-accent-strong" /> Bozze e comunicazioni
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Descrivi cosa devi scrivere: ottieni 2 varianti pronte da copiare. Nulla parte da qui.
            </p>
          </div>
          {session && (
            <button type="button" onClick={reset} className="wl-btn wl-btn--ghost wl-btn--sm"
                    data-testid="drafts-new-btn">
              <Plus size={14} className="mr-1" /> Nuova bozza
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2.5 text-[12px] text-text-secondary bg-surface-strong border border-border rounded-lg px-3.5 py-2.5"
           data-testid="drafts-disclaimer">
        <ShieldCheck size={15} className="text-accent-strong flex-shrink-0 mt-0.5" />
        <span>Rileggi sempre prima di inviare: <strong>sei tu a firmare</strong>. L'AI scrive la bozza, l'invio resta fuori da Wattline.</span>
      </div>

      {/* Form */}
      {!session && (
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4" data-testid="drafts-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {DRAFT_TYPES.map((t) => (
              <button key={t.id} type="button" onClick={() => setDraftType(t.id)}
                      data-testid={`draft-type-${t.id}`}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${
                        draftType === t.id ? "border-accent bg-accent-subtle" : "border-border hover:border-accent/50"}`}>
                <div className="text-sm font-medium text-text-primary">{t.label}</div>
                <div className="text-[10px] text-text-muted">{t.hint}</div>
              </button>
            ))}
          </div>

          {SCENARIO_CHIPS[draftType] && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="drafts-scenarios">
              <span className="text-[11px] text-text-muted">Casi tipici:</span>
              {SCENARIO_CHIPS[draftType].map(([label, prefill]) => (
                <button key={label} type="button" onClick={() => setContext(prefill)}
                        data-testid={`scenario-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-[11px] px-2 py-1 rounded-full border border-border hover:border-accent text-text-primary transition-colors">
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-[12px] text-text-muted">Tono:</span>
            {TONES.map((t) => (
              <button key={t.id} type="button" onClick={() => setTone(t.id)}
                      data-testid={`tone-${t.id}`}
                      className={`text-[12px] px-3 py-1 rounded-full border transition-colors ${
                        tone === t.id ? "border-accent bg-accent-subtle text-accent-strong font-medium"
                          : "border-border text-text-secondary hover:border-accent/50"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-[12px] font-medium text-text-primary">Destinatario e contesto (2-3 righe)</label>
            <textarea value={context} onChange={(e) => setContext(e.target.value)} rows={3}
                      data-testid="drafts-context-input"
                      placeholder="Es.: Il cliente Rossi lamenta un importo alto in bolletta; abbiamo verificato: è un conguaglio del distributore, i consumi sono corretti…"
                      className="mt-1 w-full text-sm bg-surface border border-border rounded-lg px-3 py-2 focus:outline-none focus:border-accent resize-y" />
          </div>
          <div>
            <label className="text-[12px] font-medium text-text-primary">Punti da includere (facoltativo)</label>
            <textarea value={points} onChange={(e) => setPoints(e.target.value)} rows={2}
                      data-testid="drafts-points-input"
                      placeholder="Es.: spiegare il conguaglio in parole semplici; invitare a chiamarci"
                      className="mt-1 w-full text-sm bg-surface border border-border rounded-lg px-3 py-2 focus:outline-none focus:border-accent resize-y" />
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={onGenerate}
                    disabled={busy === "generate" || context.trim().length < 10}
                    className="wl-btn wl-btn--primary" data-testid="generate-btn">
              {busy === "generate" ? (<><Loader2 size={14} className="animate-spin mr-1.5" /> Scrittura in corso…</>)
                : (<><Sparkles size={14} className="mr-1.5" /> Genera 2 bozze</>)}
            </button>
          </div>
        </div>
      )}

      {/* Varianti */}
      {session && (
        <div className="space-y-4" data-testid="drafts-result">
          <div className="text-[12px] text-text-muted">
            {TYPE_LABEL[session.draft_type]} · tono {session.tone} · «{session.context.slice(0, 80)}{session.context.length > 80 ? "…" : ""}»
            {session.revisions?.length > 0 && ` · ${session.revisions.length} ${session.revisions.length === 1 ? "revisione" : "revisioni"}`}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {session.variants.map((v, i) => (
              <div key={v.id} className="bg-surface border border-border rounded-xl p-4 flex flex-col"
                   data-testid={`variant-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[12px] font-medium text-text-primary">Variante {i + 1} — {v.title}</span>
                  <button type="button" onClick={() => copyVariant(v)}
                          className="wl-btn wl-btn--ghost wl-btn--sm" data-testid={`copy-variant-${i}`}>
                    <Copy size={13} className="mr-1" /> Copia
                  </button>
                </div>
                <pre className="text-[13px] text-text-primary whitespace-pre-wrap font-sans leading-relaxed flex-1"
                     data-testid={`variant-text-${i}`}>{v.text}</pre>
              </div>
            ))}
          </div>

          {/* Revisione */}
          <div className="bg-surface border border-border rounded-xl p-4 space-y-2.5" data-testid="drafts-revise-card">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-text-muted">Revisioni rapide:</span>
              {REVISE_CHIPS.map((c) => (
                <button key={c} type="button" disabled={busy === "revise"}
                        onClick={() => onRevise(c)}
                        data-testid={`revise-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-[11px] px-2 py-1 rounded-full border border-border hover:border-accent text-text-primary transition-colors disabled:opacity-50">
                  {c}
                </button>
              ))}
              <span className="text-[10px] text-text-muted ml-auto">ogni revisione usa 1 operazione AI</span>
            </div>
            <div className="flex gap-2">
              <input value={reviseText} onChange={(e) => setReviseText(e.target.value)}
                     placeholder="Oppure scrivi tu: es. «aggiungi che rispondiamo entro 2 giorni»"
                     data-testid="revise-input"
                     className="flex-1 h-9 text-sm bg-surface border border-border rounded-lg px-3 focus:outline-none focus:border-accent" />
              <button type="button" onClick={() => onRevise(reviseText)}
                      disabled={busy === "revise" || reviseText.trim().length < 3}
                      className="wl-btn wl-btn--secondary" data-testid="revise-btn">
                {busy === "revise" ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Wand2 size={14} className="mr-1.5" />}
                Rivedi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storico */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="drafts-history">
        <h2 className="text-base font-medium text-text-primary flex items-center gap-2 mb-3">
          <History size={15} className="text-text-muted" /> Storico bozze
        </h2>
        {historyFailed ? (
          <LoadErrorBox compact what="lo storico bozze" onRetry={loadHistory} testId="drafts-history-error" />
        ) : history.length === 0 ? (
          <p className="text-sm text-text-muted">Nessuna bozza ancora: descrivi la prima qui sopra.</p>
        ) : (
          <div className="divide-y divide-border">
            {history.map((h) => (
              <button key={h.id} type="button" onClick={() => openHistory(h.id)}
                      data-testid={`drafts-history-item-${h.id}`}
                      className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-subtle rounded px-2 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-subtle text-text-secondary flex-shrink-0">
                    {TYPE_LABEL[h.draft_type]}
                  </span>
                  <span className="text-sm text-text-primary truncate">{h.context_preview}…</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-[11px] text-text-muted">
                  {h.n_revisions > 0 && <span>{h.n_revisions} rev.</span>}
                  <span>{fmtDT(h.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
